<template>
    <v-col v-if="school?.bypass_marking" cols="10">
        <v-alert
            cols="10"
            closable
            type="info"
            density="compact"
            class="small-alert"
            text="Note: Marking has been disabled for this school."
        ></v-alert>
    </v-col>
    <template v-if="task && task.book">
        <v-row>
            <v-col cols="10">
                <v-card class="h-100">
                    <v-row>
                        <v-col>
                            <v-btn icon-size="medium" variant="text" :to="`/teacher/book/${task.book.id}/about`">
                                <template #prepend> <v-icon size="x-large">mdi-chevron-left</v-icon> </template> Go back
                            </v-btn>
                        </v-col>
                        <v-col>
                            <div class="d-flex justify-end gc-3 mt-auto">
                                <v-btn
                                    v-if="!isTeacherLed"
                                    prepend-icon="mdi-account"
                                    color="brand"
                                    @click="showManageTasksDialog = true"
                                    >Manage access</v-btn
                                >
                                <!-- Hide 'Print task' for ASPIRE temporarily -->
                                <v-menu v-if="!task.aspire" class="extra-menu">
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            v-bind="props"
                                            prepend-icon="mdi-printer"
                                            color="brand"
                                            variant="outlined"
                                        >
                                            Print
                                        </v-btn>
                                    </template>
                                    <v-list>
                                        <v-list-item v-if="task.reading_test || task.hots" @click="printTask(true)"
                                            ><v-list-item-title>Print with answers</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="printTask(false)"
                                            ><v-list-item-title>Print without answers</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-btn
                                    v-if="menuItems.length > 0"
                                    id="menu-activator"
                                    color="brand"
                                    variant="text"
                                    icon="mdi-dots-vertical"
                                />
                                <v-menu activator="#menu-activator" class="extra-menu">
                                    <v-list>
                                        <v-list-item
                                            v-for="(item, index) in menuItems"
                                            :key="index"
                                            :value="index"
                                            @click="item.action"
                                        >
                                            <template #prepend>
                                                <v-icon :icon="item.icon"></v-icon>
                                            </template>
                                            <v-list-item-title>{{ item.title }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col style="flex-grow: 2">
                            <h2 class="text-left">
                                <TaskIcon :task="task" class="mr-2" />
                                {{ task.title }}
                            </h2>
                            <v-alert v-if="isLowerLevelBook && task.hots" border="start" class="text-left mb-3"
                                >Use this task in the way that best suits your learners. Explore it through discussion,
                                small group work, writing in class, or submitting individual responses via
                                Giglets.</v-alert
                            >
                        </v-col>
                    </v-row>

                    <template v-if="!task.reading_test">
                        <template v-if="isCrossCurricular && !isBeforeQuestion">
                            <v-row no-gutters>
                                <template v-for="gridItem in crossCurricularGridItems" :key="gridItem.title">
                                    <v-col cols="12" sm="6">
                                        <h3>{{ gridItem.title }}</h3>
                                        <p>
                                            <template v-for="(text, i) in gridItem.texts" :key="`griditem-${i}`">
                                                <span>
                                                    {{ gridItem.texts[i]
                                                    }}{{ i < gridItem.texts.length - 1 ? ', ' : '' }}
                                                    <v-tooltip
                                                        v-if="gridItem.tooltips"
                                                        activator="parent"
                                                        location="top"
                                                        :text="gridItem.tooltips[i]"
                                                        max-width="300"
                                                    ></v-tooltip>
                                                </span>
                                            </template>
                                        </p>
                                    </v-col>
                                </template>
                            </v-row>
                        </template>

                        <template v-if="task.hots">
                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-remember));`"
                                ></div>
                                <h3>Remember</h3>
                                <p>{{ task.hots_remember }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Remember')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>

                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-understand));`"
                                ></div>
                                <h3 class="mt-4">Understand</h3>
                                <p>{{ task.hots_understand }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Understand')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>

                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-apply));`"
                                ></div>
                                <h3 class="mt-4">Apply</h3>
                                <p>{{ task.hots_apply }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Apply')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>

                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-analyse));`"
                                ></div>
                                <h3 class="mt-4">Analyse</h3>
                                <p>{{ task.hots_analyse }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Analyse')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>

                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-evaluate));`"
                                ></div>
                                <h3 class="mt-4">Evaluate</h3>
                                <p>{{ task.hots_evaluate }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Evaluate')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>

                            <v-card class="mb-2">
                                <div
                                    class="hots_border"
                                    :style="`background-color: rgba(var(--v-theme-hots-create));`"
                                ></div>
                                <h3 class="mt-4">Create</h3>
                                <p>{{ task.hots_create }}</p>
                                <HotsSuggestedAnswer
                                    :answer="a('Create')"
                                    :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                                />
                            </v-card>
                        </template>

                        <div v-else-if="task.aspire">
                            <template v-if="task.aspire_fields?.audience">
                                <h3 class="text-hots-remember">Audience & Purpose</h3>
                                <p>{{ task.aspire_fields?.audience }}</p>
                                <p class="text-primary font-weight-bold white-space-pre-wrap">
                                    {{ task.aspire_fields?.audience_answer }}
                                </p>
                            </template>
                            <template v-if="task.aspire_fields?.author_intent">
                                <h3 class="text-hots-remember">Author Intent</h3>
                                <p>{{ task.aspire_fields?.author_intent }}</p>
                                <p class="text-primary font-weight-bold white-space-pre-wrap">
                                    {{ task.aspire_fields?.author_intent_answer }}
                                </p>
                            </template>

                            <h3 class="text-hots-understand">Summarise</h3>
                            <p>{{ task.aspire_fields?.summarise }}</p>
                            <p class="text-primary font-weight-bold white-space-pre-wrap">
                                {{ task.aspire_fields?.summarise_answer }}
                            </p>

                            <h3 class="text-hots-apply">Patterns, Connections and Comparisons</h3>
                            <p>{{ task.aspire_fields?.pattern }}</p>
                            <p class="text-primary font-weight-bold white-space-pre-wrap">
                                {{ task.aspire_fields?.pattern_answer }}
                            </p>

                            <h3 class="text-hots-analyse">Inference</h3>
                            <p>{{ task.aspire_fields?.inference }}</p>
                            <p class="text-primary font-weight-bold white-space-pre-wrap">
                                {{ task.aspire_fields?.inference_answer }}
                            </p>

                            <h3 class="text-hots-evaluate">Reasoning</h3>
                            <p>{{ task.aspire_fields?.reasoning }}</p>
                            <p class="text-primary font-weight-bold white-space-pre-wrap">
                                {{ task.aspire_fields?.reasoning_answer }}
                            </p>

                            <h3 class="text-hots-create">Effect:</h3>
                            <p>{{ task.aspire_fields?.effect }}</p>
                            <p class="text-primary font-weight-bold white-space-pre-wrap">
                                {{ task.aspire_fields?.effect_answer }}
                            </p>
                        </div>
                    </template>
                    <template v-if="!task.hots && !task.aspire">
                        <v-row>
                            <v-col>
                                <div
                                    v-if="task.instructions"
                                    class="text-left taskinstructions"
                                    v-html="task.instructions"
                                ></div>
                                <TeacherTaskSteps :task="task" />
                            </v-col>
                        </v-row>
                    </template>
                </v-card>
            </v-col>
            <TaskStats
                v-if="!isTeacherLed"
                heading="Overview"
                :no-of-assigned="assignedPupils.length"
                :no-of-submitted="noOfSubmitted"
                :no-of-completed="noOfCompleted"
                :book="book"
                :use-image="true"
            />
        </v-row>
        <v-row v-if="isCrossCurricular && !isBeforeQuestion">
            <v-col cols="10">
                <v-card class="learning">
                    <h2>Learning</h2>

                    <template v-if="task.significant_aspects.length > 0">
                        <h3>Significant aspects of learning</h3>
                        <ul>
                            <li v-for="(sa, idx) in task.significant_aspects" :key="`sa-${idx}`">
                                <template v-if="sa.category.parent && sa.category.parent.id != 0">
                                    <strong>{{ sa.category.name }}: </strong>
                                </template>
                                {{ sa.name }}
                            </li>
                        </ul>
                    </template>

                    <template v-if="task.skills.length > 0">
                        <h3>Skills</h3>
                        <ul>
                            <li v-for="(skill, idx) of task.skills" :key="`skill-${idx}`">
                                {{ filters.capitalize(skill.description) }}
                            </li>
                        </ul>
                    </template>

                    <template v-if="task.attainment_targets.length > 0">
                        <h3>Curriculum Statements</h3>
                        <ul>
                            <li v-for="(at, idx) of task.attainment_targets" :key="`attainment_targets-${idx}`">
                                {{ filters.capitalize(at.description) }}
                            </li>
                        </ul>
                    </template>

                    <h3>Learning {{ task.curriculum.id == 2 ? 'objective' : 'intention' }}</h3>
                    <p>{{ task.learning_objective }}</p>

                    <h3>Suggested success criteria</h3>
                    <ul>
                        <li v-for="(sc, idx) of task.success_criteria" :key="`sc-${idx}`">{{ sc.criterion }}</li>
                    </ul>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="10">
                <v-card v-if="task.resources && task.resources.length > 0">
                    <h2>Additional resources</h2>
                    <ul class="book-tasks-list">
                        <li v-for="(resource, idx) in task.resources" :key="`res-${idx}`">
                            <div class="task-thumb">
                                <i class="fa-regular fa-file"></i>
                            </div>
                            <div class="task-info">
                                <div class="task-title">
                                    <a :href="resource.url">{{ resource.filename }}</a>
                                </div>
                            </div>
                        </li>
                    </ul>
                </v-card>
            </v-col>
        </v-row>
        <TeacherManageTasksDialog
            v-model="showManageTasksDialog"
            :book="book"
            :preSelectedTasks="selectedTasks"
            showOnlySelectedTasks
        ></TeacherManageTasksDialog>

        <!-- Hidden print containers  -->
        <div id="js_print" class="d-none">
            <PrintHOTS
                v-if="task.hots"
                :task="task"
                :school-name="school?.name || ''"
                :show-answers="printWithAnswers"
                :answers="answers"
            />
            <PrintNonHOTS v-else :task="task" :school-name="school?.name || ''" :show-answers="printWithAnswers" />
        </div>
    </template>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { HotsPart, HotsTaskAnswers } from '@ilteducation/giglets-task-feedback-types';
import TaskStats from '~/components/teacher/sidepanel/TaskStats.vue';
import HotsSuggestedAnswer from '~/components/teacher/HotsSuggestedAnswer.vue';
import PrintNonHOTS from '~/components/teacher/print/PrintNonHOTS.vue';
import PrintHOTS from '~/components/teacher/print/PrintHOTS.vue';
import print from '@/utils/print';
import { Task, TaskOverview } from '@ilteducation/giglets-types';
import { GigletsBookV1 } from '@ilteducation/content-registry-types';

type MenuItem = {
    title: string;
    icon: string;
    action: () => void;
};

type CrossCuricularItem = {
    title: string;
    texts: string[];
    tooltips?: string[];
};

const route = useRoute('/teacher/task/[[task_id]]/view-task');
definePage({ meta: { area: 'books' } });
const filters = useFilters();
const router = useRouter();
const store = useTeacherStore();
store.fetchSchool();
store.fetchAssignedTasks();
store.fetchPupilTasks();
const { school, assignedTasks, pupilTasks } = storeToRefs(store);

const showManageTasksDialog = ref(false);
const printWithAnswers = ref(false);

const pupilTasksForTask = computed(() => pupilTasks.value.filter((pt) => pt.task.id === task.value?.id));
const noOfSubmitted = computed(() => pupilTasksForTask.value.filter((pt) => pt.status === 2).length);
const noOfCompleted = computed(() => pupilTasksForTask.value.filter((pt) => pt.status >= 3).length);

const crossCurricularGridItems = computed(() => {
    const tempArray: CrossCuricularItem[] = [];
    if (task.value.curriculum.id === 1) {
        tempArray.push({ title: 'Curriculum area', texts: task.value.curriculum_areas.map((a) => a.name) });
        tempArray.push({ title: 'Level', texts: [task.value.level ? task.value.level.title : ''] });
    } else if (task.value.curriculum.id === 2) {
        tempArray.push({ title: 'Subjects', texts: task.value.subjects.map((s) => s.name) });
        tempArray.push({ title: 'Year', texts: [task.value.year_group ? task.value.year_group.name : ''] });
    } else if (task.value.curriculum.id === 3) {
        tempArray.push({ title: 'Phase / Stage', texts: [task.value.phase_stage ? task.value.phase_stage.name : ''] });
        tempArray.push({ title: 'Year', texts: [task.value.year ? task.value.year.name : ''] });
        tempArray.push({ title: 'Subject / Strand', texts: task.value.strands.map((s) => s.name) });
    } else if (task.value.curriculum.id === 4) {
        tempArray.push({ title: 'Language / Stage', texts: [task.value.language.name] });
        tempArray.push({ title: 'Stage', texts: [task.value.irish_stage ? task.value.irish_stage.name_en : ''] });
        tempArray.push({
            title: 'Strand',
            texts: task.value.number_labels.map((nl) => nl.strand.name_en),
            tooltips: task.value.number_labels.map((nl) => nl.strand.name_ie),
        });
        tempArray.push({
            title: 'Element',
            texts: task.value.number_labels.map((nl) => nl.element.name_en),
            tooltips: task.value.number_labels.map((nl) => nl.element.name_ie),
        });
    }
    if (task.value.curriculum.id !== 4) {
        tempArray.push({ title: 'Topic', texts: task.value.topics.map((t) => t.title) });
        if (task.value.curriculum.id === 1) {
            tempArray.push({
                title: 'Es&Os',
                texts: task.value.esandos.map((eando) => eando.code),
                tooltips: task.value.esandos.map((eando) => eando.code + ': ' + eando.description),
            });
        }
    }
    if (task.value.reading_strategies.length > 0) {
        tempArray.push({ title: 'Reading strategies', texts: task.value.reading_strategies.map((r) => r.name) });
    }
    if (task.value.curriculum.id === 4) {
        tempArray.push({
            title: 'Learning outcomes',
            texts: task.value.irish_learning_outcomes.map((lo) => lo.outcome_ie),
        });
    } else {
        tempArray.push({ title: 'Language', texts: [task.value.language.name] });
    }

    return tempArray;
});

const assignedPupils = computed(() => {
    return assignedTasks.value.find((assignedTask) => assignedTask.task_id === task.value?.id)?.assigned_to || [];
});

const taskId = ref(parseInt(route.params.task_id + '') || 0);
const task = ref(await $fetch<Task>(`/api/tasks/${taskId.value}`));

const menuItems = ref<MenuItem[]>([]);
if (!task.value.aspire) {
    if (school && school.value && school.value.type.abbrv === 'PREM') {
        menuItems.value.push({ title: 'Copy task', icon: 'mdi-content-copy', action: copyTask });
        if (task.value.owner && task.value.owner.id !== 0) {
            // Teacher created task
            menuItems.value.push({ title: 'Edit task', icon: 'mdi-pencil', action: editTask });
        }
    } else {
        menuItems.value.push({
            title: 'Copy task',
            icon: 'mdi-lock',
            action: () => {
                router.push(`/teacher/tlc-upgrade`);
            },
        });
    }
}

const isTeacherLed = computed(() => task.value.types.some((t) => t.code === 'teacher'));

const { getImageUrl } = useImageCacheReset();
const { getBook } = useContentSearchStore();
const book = ref<GigletsBookV1>(await getBook(task.value.book.id + ''));
const { changeBackground } = useBackground();
if (book.value?.backgroundUrl) {
    changeBackground(getImageUrl(book.value.backgroundUrl, book.value), 'revert-layer', false, true);
}
const selectedTasks = ref<TaskOverview[]>([]);
selectedTasks.value.push(task.value);

const isCrossCurricular = computed(() => {
    return !task.value.hots && !task.value.reading_test && !task.value.aspire;
});

const isBeforeQuestion = computed(() => {
    return task.value && task.value.types.map((tt) => tt.code).includes('before');
});

async function copyTask() {
    if (!task.value) {
        return;
    }
    const { id: newId } = await $fetch<{ id: number }>(`/api/teacher/current/tasks/${task.value.id}/clone`, {
        method: 'POST',
    });
    router.push(`/teacher/task/${newId}/edit-task`);
}

function editTask() {
    if (!task.value) {
        return;
    }
    router.push(`/teacher/task/${task.value.id}/edit-task`);
}

const answers = ref<HotsTaskAnswers>();
const isLoadingAutomatedAnswers = ref(false);

const { getAutomatedAnswersForHotsTask } = useAutomatedMarking();

watch(
    task,
    async (task) => {
        if (!task || !task.hots) {
            return;
        }
        isLoadingAutomatedAnswers.value = true;
        try {
            answers.value = await getAutomatedAnswersForHotsTask(task);
        } finally {
            isLoadingAutomatedAnswers.value = false;
        }
    },
    { immediate: true },
);

const a = (part: HotsPart): string | undefined => {
    return answers.value?.answers.find((a) => a.questionPart === part)?.answer;
};

const isLowerLevelBook = computed(() => {
    return task.value.book.level && task.value.book.level.id <= 4;
});

function printTask(withAnswers: boolean) {
    printWithAnswers.value = withAnswers;
    nextTick(() => print('js_print'));
}
</script>
<style lang="scss" scoped>
:deep(.v-alert__prepend) {
    margin-top: auto;
    margin-bottom: auto;
}

.small-alert {
    padding: 2px 8px;
    min-height: 24px;
}

.extra-menu :deep(.v-overlay__content) {
    border-radius: 12px;
}

.taskinstructions :deep(p) {
    margin: 0 0 1em 0;
    line-height: 1.5em;
    color: #60787f;
}

.v-card p {
    margin: 0 0 1em 0;
    line-height: 1.5em;
    color: #60787f;
}

.learning :deep(h3) {
    margin: 1em 0 0.5em 0;
    color: #2f3b3f !important;
    text-shadow: none !important;
}

.v-card :deep(ul),
.v-card :deep(ol) {
    margin: 0 0 1em 0;
    color: #60787f;
}

.hots_border {
    opacity: 0.7;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    width: 8px;
}
</style>
